import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import "./authExperience.css";

import Dashboard from "./pages/Dashboard";
import Cameras from "./features/cameras";
import Users from "./features/users";
import Billing from "./features/billing";
import Invoices from "./features/invoices";
import PaymentMethods from "./features/payments";
import AccessRoles from "./features/access-roles";
import Alerts from "./features/alerts";
import {
  PortalRole,
  roleFromTokenClaims,
} from "./auth/portalAccess";
import { PortalRoleProvider } from "./auth/PortalRoleContext";

function QumraMark() {
  return (
    <svg
      className="qumra-auth-logo"
      viewBox="0 0 72 72"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="32"
        cy="32"
        r="22"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray="98 42"
        transform="rotate(-38 32 32)"
      />
      <circle
        cx="32"
        cy="32"
        r="10"
        stroke="currentColor"
        strokeWidth="5"
        opacity="0.68"
      />
      <circle cx="32" cy="32" r="3.8" fill="#eef9ff" />
      <path
        d="M47 47 61 61"
        stroke="currentColor"
        strokeWidth="8"
        strokeLinecap="round"
      />
    </svg>
  );
}

type AuthHeadingProps = {
  eyebrow: string;
  title: string;
  description: string;
};

function AuthHeading({ eyebrow, title, description }: AuthHeadingProps) {
  return (
    <div className="qumra-auth-heading">
      <span className="qumra-auth-heading__eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
  );
}

const authenticatorComponents = {
  Header() {
    return (
      <header className="qumra-auth-brand">
        <QumraMark />
        <div className="qumra-auth-wordmark">QUMRA</div>
        <div className="qumra-auth-portal">ADMIN</div>
      </header>
    );
  },
  SignIn: {
    Header() {
      return (
        <AuthHeading
          eyebrow="Protected administration"
          title="Welcome back"
          description="Sign in to manage customers, users, cameras, and system access."
        />
      );
    },
  },
  ForgotPassword: {
    Header() {
      return (
        <AuthHeading
          eyebrow="Account recovery"
          title="Reset your password"
          description="Enter your email and Qumra will send a verification code."
        />
      );
    },
  },
  ConfirmResetPassword: {
    Header() {
      return (
        <AuthHeading
          eyebrow="Account recovery"
          title="Create a new password"
          description="Enter the verification code and choose your new password."
        />
      );
    },
  },
  ForceNewPassword: {
    Header() {
      return (
        <AuthHeading
          eyebrow="First sign-in"
          title="Secure your account"
          description="Your temporary password was accepted. Create a permanent password to continue."
        />
      );
    },
  },
  Footer() {
    return (
      <footer className="qumra-auth-footer">
        <span className="qumra-auth-security">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3 19 6v5c0 4.6-2.8 8.2-7 10-4.2-1.8-7-5.4-7-10V6l7-3Z"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="m9.4 12 1.7 1.7 3.7-4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Protected by Qumra secure identity services
        </span>
        <small>© 2026 Qumra Vision Inc. All rights reserved.</small>
      </footer>
    );
  },
};

const authenticatorFormFields = {
  signIn: {
    username: {
      label: "Email",
      placeholder: "name@company.com",
    },
    password: {
      label: "Password",
      placeholder: "Enter your password",
    },
  },
  forceNewPassword: {
    password: {
      label: "New password",
      placeholder: "Create a permanent password",
    },
    confirm_password: {
      label: "Confirm new password",
      placeholder: "Enter the new password again",
    },
  },
};

type AuthenticatedPortalProps = {
  user?: any;
  signOut?: () => void | Promise<void>;
};

type AccessState =
  | { status: "LOADING"; role: null }
  | { status: "READY"; role: PortalRole }
  | { status: "DENIED"; role: null };

function PortalAccessNotice({
  title,
  message,
  signOut,
}: {
  title: string;
  message: string;
  signOut?: () => void | Promise<void>;
}) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        background: "#f5f7fb",
      }}
    >
      <section
        style={{
          width: "min(520px, 100%)",
          padding: "32px",
          borderRadius: "16px",
          background: "#ffffff",
          boxShadow: "0 14px 40px rgba(15, 23, 42, 0.12)",
        }}
      >
        <h1 style={{ marginTop: 0 }}>{title}</h1>
        <p style={{ lineHeight: 1.6 }}>{message}</p>
        {signOut && (
          <button
            type="button"
            onClick={() => void signOut()}
            style={{
              marginTop: "12px",
              padding: "10px 16px",
              border: 0,
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Sign out
          </button>
        )}
      </section>
    </main>
  );
}

function AuthenticatedPortal({ user, signOut }: AuthenticatedPortalProps) {
  const [access, setAccess] = useState<AccessState>({
    status: "LOADING",
    role: null,
  });

  useEffect(() => {
    let active = true;

    cognitoUserPoolsTokenProvider.getTokens()
      .then((tokens) => {
        if (!active) return;
        const claims = tokens?.idToken?.payload as
          | Record<string, unknown>
          | undefined;
        const role = roleFromTokenClaims(claims);
        setAccess(
          role
            ? { status: "READY", role }
            : { status: "DENIED", role: null },
        );
      })
      .catch(() => {
        if (active) setAccess({ status: "DENIED", role: null });
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (access.status !== "READY" || access.role !== "VIEWER" || !signOut) {
      return;
    }

    void Promise.resolve(signOut()).catch(() => {
      // Keep the access notice visible if local sign-out fails unexpectedly.
    });
  }, [access, signOut]);

  if (access.status === "LOADING") {
    return (
      <PortalAccessNotice
        title="Checking account access"
        message="Qumra is confirming your portal role."
      />
    );
  }

  if (access.status === "DENIED") {
    return (
      <PortalAccessNotice
        title="Admin Portal access unavailable"
        message="This account does not have a recognized Qumra Admin Portal role."
        signOut={signOut}
      />
    );
  }

  if (access.role === "VIEWER") {
    return (
      <PortalAccessNotice
        title="Viewer access is not available here"
        message="Viewer accounts can access assigned cameras through Qumra Vision only. You are being signed out of the Admin Portal."
      />
    );
  }

  const canManageUsers =
    access.role === "OWNER" ||
    access.role === "INTEGRATOR" ||
    access.role === "ADMIN";
  const canManageAlerts =
    access.role === "OWNER" ||
    access.role === "INTEGRATOR" ||
    access.role === "ADMIN";
  const canUsePlatformBilling =
    access.role === "OWNER" || access.role === "INTEGRATOR";

  return (
    <BrowserRouter>
      <PortalRoleProvider role={access.role}>
      <Routes>
        <Route
          path="/dashboard"
          element={<Dashboard user={user} onSignOut={signOut} />}
        />
        <Route
          path="/cameras"
          element={<Cameras user={user} onSignOut={signOut} />}
        />
        <Route
          path="/billing"
          element={<Billing user={user} onSignOut={signOut} />}
        />
        {canUsePlatformBilling ? (
          <>
            <Route
              path="/invoices"
              element={<Invoices user={user} onSignOut={signOut} />}
            />
            <Route
              path="/payment-methods"
              element={<PaymentMethods user={user} onSignOut={signOut} />}
            />
          </>
        ) : (
          <>
            <Route path="/invoices" element={<Navigate to="/cameras" replace />} />
            <Route path="/payment-methods" element={<Navigate to="/cameras" replace />} />
          </>
        )}
        {canManageAlerts && (
          <Route
            path="/alerts"
            element={<Alerts user={user} onSignOut={signOut} />}
          />
        )}
        {!canManageAlerts && (
          <Route path="/alerts" element={<Navigate to="/cameras" replace />} />
        )}
        {canManageUsers && (
          <>
            <Route
              path="/users"
              element={<Users user={user} onSignOut={signOut} />}
            />
            <Route
              path="/access-roles"
              element={<AccessRoles user={user} onSignOut={signOut} />}
            />
          </>
        )}
        {!canManageUsers && (
          <>
            <Route path="/users" element={<Navigate to="/cameras" replace />} />
            <Route path="/access-roles" element={<Navigate to="/cameras" replace />} />
          </>
        )}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </PortalRoleProvider>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Authenticator
      hideSignUp
      components={authenticatorComponents}
      formFields={authenticatorFormFields}
    >
      {({ signOut, user }) => (
        <AuthenticatedPortal user={user} signOut={signOut} />
      )}
    </Authenticator>
  );
}
